import { LitElement, html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import {
  api,
  type ImportPreview,
  type ParsedPlayer,
} from '../../services/api';

type WizardStep = 'upload' | 'preview' | 'confirm' | 'complete';

@customElement('roster-import-wizard')
export class RosterImportWizard extends LitElement {
  createRenderRoot() {
    return this;
  }

  @property()
  teamId = '';

  @property()
  seasonId = '';

  @state()
  private step: WizardStep = 'upload';

  @state()
  private file: File | null = null;

  @state()
  private preview: ImportPreview | null = null;

  @state()
  private loading = false;

  @state()
  private error = '';

  @state()
  private skipDuplicates = false;

  @state()
  private importedCount = 0;

  private handleFileSelect(e: Event) {
    const input = e.target as HTMLInputElement;
    this.file = input.files?.[0] || null;
    this.error = '';
  }

  private handleDragOver(e: DragEvent) {
    e.preventDefault();
    e.stopPropagation();
  }

  private handleDrop(e: DragEvent) {
    e.preventDefault();
    e.stopPropagation();
    const files = e.dataTransfer?.files;
    if (files && files.length > 0) {
      const file = files[0];
      if (file.name.endsWith('.csv')) {
        this.file = file;
        this.error = '';
      } else {
        this.error = 'Please upload a CSV file';
      }
    }
  }

  private async uploadFile() {
    if (!this.file) return;
    this.loading = true;
    this.error = '';
    try {
      this.preview = await api.uploadRosterCSV(
        this.teamId,
        this.file,
        this.seasonId || undefined
      );
      this.step = 'preview';
    } catch (err) {
      this.error = err instanceof Error ? err.message : 'Upload failed';
    } finally {
      this.loading = false;
    }
  }

  private async confirmImport() {
    if (!this.preview) return;
    this.loading = true;
    this.error = '';
    try {
      const result = await api.confirmRosterImport(
        this.preview.import_id,
        this.skipDuplicates
      );
      this.importedCount = result.player_count;
      this.step = 'complete';
      this.dispatchEvent(new CustomEvent('import-complete'));
    } catch (err) {
      this.error = err instanceof Error ? err.message : 'Import failed';
    } finally {
      this.loading = false;
    }
  }

  private async cancelImport() {
    if (!this.preview) return;
    try {
      await api.cancelRosterImport(this.preview.import_id);
    } catch (err) {
      console.error('Failed to cancel import:', err);
    }
    this.reset();
  }

  private reset() {
    this.step = 'upload';
    this.file = null;
    this.preview = null;
    this.error = '';
    this.skipDuplicates = false;
    this.importedCount = 0;
  }

  render() {
    return html`
      <div class="card bg-base-100 shadow-lg">
        <div class="card-body">
          <h2 class="card-title">Import Roster from CSV</h2>

          <!-- Steps indicator -->
          <ul class="steps steps-horizontal mb-6 w-full">
            <li
              class="step ${this.step !== 'upload' ? 'step-primary' : ''}"
            >
              Upload
            </li>
            <li
              class="step ${['preview', 'confirm', 'complete'].includes(
                this.step
              )
                ? 'step-primary'
                : ''}"
            >
              Preview
            </li>
            <li
              class="step ${this.step === 'complete' ? 'step-primary' : ''}"
            >
              Complete
            </li>
          </ul>

          ${this.error
            ? html`
                <div class="alert alert-error mb-4">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    class="stroke-current shrink-0 h-6 w-6"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                  <span>${this.error}</span>
                </div>
              `
            : nothing}
          ${this.step === 'upload' ? this.renderUpload() : nothing}
          ${this.step === 'preview' ? this.renderPreview() : nothing}
          ${this.step === 'complete' ? this.renderComplete() : nothing}
        </div>
      </div>
    `;
  }

  private renderUpload() {
    return html`
      <div
        class="border-2 border-dashed border-base-300 rounded-lg p-8 text-center"
        @dragover=${this.handleDragOver}
        @drop=${this.handleDrop}
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          class="mx-auto h-12 w-12 text-base-content/50"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
          />
        </svg>
        <p class="mt-4 text-base-content/70">
          Drag and drop a CSV file, or click to select
        </p>
        <input
          type="file"
          accept=".csv"
          class="file-input file-input-bordered file-input-primary mt-4"
          @change=${this.handleFileSelect}
        />
        ${this.file
          ? html`
              <p class="mt-4 font-medium">${this.file.name}</p>
            `
          : nothing}
      </div>

      <div class="alert alert-info mt-4">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          class="stroke-current shrink-0 h-6 w-6"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
          />
        </svg>
        <span>
          Upload a RAMP CSV export. Expected columns: First Name, Last Name,
          DOB, Jersey, Division, Subdivision, Club, Parent info, etc.
        </span>
      </div>

      <div class="card-actions justify-end mt-4">
        <button
          class="btn btn-primary ${this.loading ? 'loading' : ''}"
          ?disabled=${!this.file || this.loading}
          @click=${this.uploadFile}
        >
          ${this.loading ? 'Uploading...' : 'Upload & Preview'}
        </button>
      </div>
    `;
  }

  private renderPreview() {
    if (!this.preview) return nothing;

    const { summary, players } = this.preview;
    const hasErrors = players.some((p) => p.errors && p.errors.length > 0);
    const hasDuplicates = players.some((p) => p.is_duplicate);

    return html`
      <!-- Summary -->
      <div class="stats shadow mb-4 w-full">
        <div class="stat">
          <div class="stat-title">Total Rows</div>
          <div class="stat-value text-lg">${summary.total_rows}</div>
        </div>
        <div class="stat">
          <div class="stat-title">New Players</div>
          <div class="stat-value text-lg text-success">
            ${summary.new_players}
          </div>
        </div>
        <div class="stat">
          <div class="stat-title">Updates</div>
          <div class="stat-value text-lg text-warning">
            ${summary.updated_players}
          </div>
        </div>
        <div class="stat">
          <div class="stat-title">Errors</div>
          <div class="stat-value text-lg text-error">${summary.errors}</div>
        </div>
      </div>

      ${hasDuplicates
        ? html`
            <div class="form-control mb-4">
              <label class="label cursor-pointer">
                <span class="label-text">
                  Skip duplicate players (don't update existing records)
                </span>
                <input
                  type="checkbox"
                  class="checkbox checkbox-primary"
                  .checked=${this.skipDuplicates}
                  @change=${(e: Event) => {
                    this.skipDuplicates = (e.target as HTMLInputElement)
                      .checked;
                  }}
                />
              </label>
            </div>
          `
        : nothing}

      <!-- Players table -->
      <div class="overflow-x-auto max-h-96">
        <table class="table table-zebra table-sm">
          <thead class="sticky top-0 bg-base-100">
            <tr>
              <th>Name</th>
              <th>DOB</th>
              <th>Jersey</th>
              <th>Status</th>
              <th>Issues</th>
            </tr>
          </thead>
          <tbody>
            ${players.map((player) => this.renderPlayerRow(player))}
          </tbody>
        </table>
      </div>

      <div class="card-actions justify-between mt-4">
        <button class="btn btn-ghost" @click=${this.cancelImport}>
          Cancel
        </button>
        <button
          class="btn btn-primary ${this.loading ? 'loading' : ''}"
          ?disabled=${this.loading || hasErrors}
          @click=${this.confirmImport}
        >
          ${this.loading ? 'Importing...' : 'Confirm Import'}
        </button>
      </div>
    `;
  }

  private renderPlayerRow(player: ParsedPlayer) {
    const hasErrors = player.errors && player.errors.length > 0;
    const rowClass = hasErrors
      ? 'bg-error/20'
      : player.is_duplicate
        ? 'bg-warning/20'
        : '';

    return html`
      <tr class=${rowClass}>
        <td>${player.name}</td>
        <td>${player.dob || '-'}</td>
        <td>${player.jersey ?? '-'}</td>
        <td>
          ${hasErrors
            ? html`<span class="badge badge-error">Error</span>`
            : player.is_duplicate
              ? html`<span class="badge badge-warning">Update</span>`
              : html`<span class="badge badge-success">New</span>`}
          ${player.is_staff
            ? html`<span class="badge badge-info ml-1">Staff</span>`
            : nothing}
        </td>
        <td>
          ${hasErrors
            ? html`<span class="text-error text-sm"
                >${player.errors?.join(', ')}</span
              >`
            : nothing}
        </td>
      </tr>
    `;
  }

  private renderComplete() {
    return html`
      <div class="text-center py-8">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          class="mx-auto h-16 w-16 text-success"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
          />
        </svg>
        <h3 class="text-xl font-semibold mt-4">Import Complete!</h3>
        <p class="text-base-content/70 mt-2">
          Successfully imported ${this.importedCount} players.
        </p>
        <button class="btn btn-primary mt-6" @click=${this.reset}>
          Import Another
        </button>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'roster-import-wizard': RosterImportWizard;
  }
}
